/**
 * Stock Check (Suri, 2026-10-06): warehouse staff scan a SKU (or type it) to
 * see Mintsoft's stock for it and WHERE it is. Read-only — nothing is saved
 * or sent to Mintsoft. Scan again to check the next SKU; Refresh re-reads
 * Mintsoft instead of using the last 20 seconds' copy.
 */
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, formatTime, qs } from '../dom.js';
import { signal } from '../feedback.js';
import type { StockLookupResult } from '../types.js';

let cachedRole: 'OPERATOR' | 'SUPERVISOR' | 'ADMIN' | null = null;
async function currentRole(): Promise<'OPERATOR' | 'SUPERVISOR' | 'ADMIN'> {
  if (cachedRole) return cachedRole;
  try {
    cachedRole = (await api.me()).role;
  } catch {
    cachedRole = 'OPERATOR';
  }
  return cachedRole;
}

const CAMERA_SCAN_HINTS = new Map<DecodeHintType, unknown>([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128]],
]);

/**
 * The stock-check UI as a reusable panel (scan box + camera + result card +
 * Refresh), so the same thing runs as its own screen AND as a sheet opened
 * from the ASN scan screen. Read-only.
 */
export interface StockLookupPanel {
  focus(): void;
  destroy(): void;
}

export function createStockLookupPanel(host: HTMLElement, opts: { showRefresh?: boolean } = {}): StockLookupPanel {
  host.innerHTML = `
    <p class="hint">Scan a barcode or type a SKU to see how many Mintsoft has and which locations they are in.</p>
    <div class="scan-input-wrap">
      <input type="text" id="lookupInput" placeholder="Scan or type SKU / barcode" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />
      <button type="button" class="ghost camera-btn" id="lookupCameraBtn" aria-label="Scan with camera">📷</button>
    </div>
    <div id="lookupResult"></div>
    <button class="ghost big" id="lookupRefreshBtn" disabled style="margin-top:0.5rem; ${opts.showRefresh === false ? 'display:none;' : ''}">Refresh from Mintsoft</button>
  `;

  const scanInput = qs<HTMLInputElement>(host, '#lookupInput');
  const resultEl = qs<HTMLDivElement>(host, '#lookupResult');
  const refreshBtn = qs<HTMLButtonElement>(host, '#lookupRefreshBtn');
  let lastQuery = '';
  let lastSku = '';
  let lastProductId = 0;
  let destroyed = false;
  let stopCamera: (() => void) | null = null;
  let requestSeq = 0;

  function focusInput(): void {
    if (!destroyed) scanInput.focus({ preventScroll: true });
  }

  refreshBtn.addEventListener('click', () => {
    if (lastQuery) void lookup(lastQuery, true);
  });

  async function lookup(query: string, refresh = false): Promise<void> {
    lastQuery = query;
    const seq = ++requestSeq; // ignore a slow earlier answer if a newer scan has been made
    resultEl.innerHTML = `<div class="lookup-card"><p class="hint center">Checking Mintsoft for ${escapeHtml(query)}…</p></div>`;
    try {
      const r = await api.stockLookup(query, refresh);
      if (seq !== requestSeq) return;
      render(r);
      refreshBtn.disabled = false;
      signal('good');
    } catch (err) {
      if (seq !== requestSeq) return;
      const notFound = err instanceof ApiError && err.code === 'PRODUCT_NOT_FOUND';
      resultEl.innerHTML = `<div class="lookup-card"><p class="error-text">${notFound ? `Nothing found for "${escapeHtml(query)}".` : escapeHtml(describeError(err))}</p><p class="hint">${notFound ? 'Check the barcode or SKU and scan again.' : 'Tap Refresh to try again.'}</p></div>`;
      refreshBtn.disabled = false;
      signal('bad');
    }
    focusInput();
  }

  function render(r: StockLookupResult): void {
    lastSku = r.sku;
    lastProductId = r.productId;
    const cards = r.warehouses
      .map((w) => {
        const locs =
          w.locations === null
            ? '<p class="lookup-note">Locations could not be read from Mintsoft right now — the totals above are still correct. Tap Refresh to retry.</p>'
            : w.locations.length === 0
              ? '<p class="hint">Mintsoft lists no stock in any location.</p>'
              : w.locations
                  .map(
                    (l) => `<div class="lookup-loc ${l.isPickLocation ? 'pick' : ''}">
                      <div><div class="name">${escapeHtml(l.name)}</div>${l.allocated > 0 ? `<div class="sub">${l.allocated} allocated to orders here</div>` : ''}</div>
                      <div class="qty">${l.quantity}</div>
                      <button type="button" class="ghost fix-btn" data-loc="${escapeHtml(l.name)}">Correct</button>
                    </div>`,
                  )
                  .join('');
        const mismatch =
          w.locationsTotal !== null && w.locationsTotal !== w.onHand
            ? `<p class="lookup-note">Locations add up to ${w.locationsTotal}, but Mintsoft's on-hand total is ${w.onHand} — some stock may be unassigned or in transit.</p>`
            : '';
        return `<div class="lookup-card">
          ${r.warehouses.length > 1 ? `<div class="hint">Warehouse ${w.warehouseId}</div>` : ''}
          <div class="lookup-totals">
            <div><div class="n">${w.stockLevel}</div><div class="l">stock level</div></div>
            <div><div class="n">${w.onHand}</div><div class="l">on hand</div></div>
            <div><div class="n">${w.allocated}</div><div class="l">allocated</div></div>
          </div>
          <h3 style="margin:1rem 0 0.25rem;">Locations</h3>
          ${locs}
          ${mismatch}
        </div>`;
      })
      .join('');
    resultEl.innerHTML = `<div class="lookup-card"><h2>${escapeHtml(r.sku)}</h2>${r.productName ? `<div class="hint">${escapeHtml(r.productName)}</div>` : ''}<div class="hint">Read from Mintsoft ${escapeHtml(formatTime(r.readAt))}</div></div>${cards || '<div class="lookup-card"><p class="hint">Mintsoft has no stock record for this product yet.</p></div>'}`;
  }

  resultEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.fix-btn');
    if (btn && lastSku) openCorrectModal(lastSku, lastProductId, btn.dataset.loc ?? '');
  });

  /** Correct the quantity in ONE location: same record as a Stock Count scan; a supervisor/admin sends it to Mintsoft straight away, an operator saves it for a supervisor to apply. */
  function openCorrectModal(sku: string, productId: number, loc: string): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `<div class="modal"><h2>Correct stock</h2>
      <div class="hint">${escapeHtml(sku)} in <b>${escapeHtml(loc)}</b></div>
      <p class="hint" id="fixExpected">Reading Mintsoft...</p>
      <label for="fixQty">How many are physically in this location?</label>
      <input type="number" inputmode="numeric" min="0" id="fixQty" style="font-size:1.6rem; width:100%; padding:0.5rem;" />
      <p class="error-text" id="fixError"></p>
      <div class="footer-actions"><button class="ghost" id="fixCancel">Cancel</button><button id="fixSend" disabled>Send to Mintsoft</button></div></div>`;
    document.body.appendChild(backdrop);
    const qtyEl = qs<HTMLInputElement>(backdrop, '#fixQty');
    const expEl = qs<HTMLParagraphElement>(backdrop, '#fixExpected');
    const errEl = qs<HTMLParagraphElement>(backdrop, '#fixError');
    const sendBtn = qs<HTMLButtonElement>(backdrop, '#fixSend');
    let canApply = false;
    const close = () => {
      backdrop.remove();
      focusInput();
    };
    qs<HTMLButtonElement>(backdrop, '#fixCancel').addEventListener('click', close);
    void (async () => {
      canApply = (await currentRole()) !== 'OPERATOR';
      sendBtn.textContent = canApply ? 'Send to Mintsoft' : 'Save for supervisor';
      try {
        const x = await api.expectedStockCount({ sku, productId, location: loc });
        expEl.textContent = x.expectedQuantity === null ? 'Mintsoft quantity for this location could not be read.' : `Mintsoft has ${x.expectedQuantity} here.`;
        if (x.expectedQuantity !== null) qtyEl.value = String(x.expectedQuantity);
      } catch (err) {
        expEl.textContent = '';
        errEl.textContent = describeError(err);
      }
      sendBtn.disabled = false;
      qtyEl.focus();
      qtyEl.select();
    })();
    sendBtn.addEventListener('click', async () => {
      const n = Number(qtyEl.value);
      if (qtyEl.value.trim() === '' || !Number.isInteger(n) || n < 0) {
        errEl.textContent = 'Enter a whole number (0 or more).';
        return;
      }
      sendBtn.disabled = true;
      errEl.textContent = '';
      try {
        const rec = await api.recordStockCount({ sku, productId, location: loc, countedQuantity: n });
        if (rec.kind !== 'RECORDED') throw new Error(rec.kind === 'LOCATION_NOT_FOUND' ? `No Mintsoft location "${rec.location}".` : 'Product not found in Mintsoft.');
        if (!canApply) {
          signal('good');
          close();
          resultEl.insertAdjacentHTML('afterbegin', `<p class="hint center">Saved ${n} for ${escapeHtml(loc)} - waiting for a supervisor to apply it.</p>`);
          return;
        }
        const res = (await api.applyStockCounts([rec.stockCount.id]))[0];
        if (res?.outcome === 'APPLIED') {
          signal('good');
          close();
          void lookup(lastQuery, true);
        } else {
          signal('bad');
          errEl.textContent = res?.message ?? 'Mintsoft did not accept the change. The count is saved as pending - a supervisor can retry it in the admin Stock Counts page.';
          sendBtn.disabled = false;
        }
      } catch (err) {
        signal('bad');
        errEl.textContent = describeError(err);
        sendBtn.disabled = false;
      }
    });
  }

  scanInput.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = scanInput.value.trim();
    scanInput.value = '';
    if (value) void lookup(value);
  });
  scanInput.addEventListener('blur', () => {
    setTimeout(() => {
      if (!document.querySelector('.modal-backdrop')) focusInput();
    }, 250);
  });
  focusInput();

  qs<HTMLButtonElement>(host, '#lookupCameraBtn').addEventListener('click', () => openCameraModal());

  function openCameraModal(): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    if (!navigator.mediaDevices?.getUserMedia) {
      backdrop.innerHTML = `<div class="modal"><h2>Camera scanning unavailable</h2><p>This browser doesn't support camera access. Use the handheld scanner or type the barcode.</p><button class="ghost" id="closeBtn">Close</button></div>`;
      document.body.appendChild(backdrop);
      qs<HTMLButtonElement>(backdrop, '#closeBtn').addEventListener('click', () => {
        backdrop.remove();
        focusInput();
      });
      return;
    }
    backdrop.innerHTML = `<div class="modal camera-modal"><h2>Scan with Camera</h2><p class="hint" id="cameraStatus">Starting camera…</p><video id="cameraVideo" autoplay playsinline muted></video><button class="ghost" id="cancelCamera" style="margin-top:0.75rem;">Cancel</button></div>`;
    document.body.appendChild(backdrop);
    const video = qs<HTMLVideoElement>(backdrop, '#cameraVideo');
    const statusEl = qs<HTMLParagraphElement>(backdrop, '#cameraStatus');
    let controls: { stop: () => void } | null = null;
    let stopped = false;
    const close = () => {
      if (!stopped) {
        stopped = true;
        controls?.stop();
        stopCamera = null;
      }
      backdrop.remove();
      focusInput();
    };
    stopCamera = close;
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
    qs<HTMLButtonElement>(backdrop, '#cancelCamera').addEventListener('click', close);
    const reader = new BrowserMultiFormatReader(CAMERA_SCAN_HINTS);
    let handled = false;
    void (async () => {
      try {
        controls = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, video, (result) => {
          if (stopped || handled || !result) return;
          handled = true;
          const value = result.getText();
          close();
          void lookup(value);
        });
        if (stopped) {
          controls.stop();
          return;
        }
        statusEl.textContent = 'Point the camera at a barcode.';
      } catch {
        statusEl.classList.add('error-text');
        statusEl.textContent = "Couldn't access the camera. Check permissions and try again.";
      }
    })();
  }

  return {
    focus: focusInput,
    destroy: () => {
      destroyed = true;
      stopCamera?.();
    },
  };
}

export async function renderStockLookup(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  container.appendChild(div);
  div.innerHTML = `
    <div class="topbar"><h1>Stock Check</h1><button class="ghost" id="backBtn">Home</button></div>
    <div id="panel"></div>
  `;
  const panel = createStockLookupPanel(qs<HTMLDivElement>(div, '#panel'));
  qs<HTMLButtonElement>(div, '#backBtn').addEventListener('click', () => {
    location.hash = '#/';
  });
  const cleanup = () => {
    panel.destroy();
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}

/** Opens the stock check as a bottom sheet over the current screen (used from the ASN scan screen). `onClose` runs after it closes so the caller can refocus its own scan box. */
export function openStockCheckSheet(onClose: () => void): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = `<div class="modal"><div style="display:flex;justify-content:space-between;align-items:center;"><h2 style="margin:0;">Stock Check</h2><button class="ghost" id="closeStockCheck">Close</button></div><div id="sheetPanel"></div></div>`;
  document.body.appendChild(backdrop);
  const panel = createStockLookupPanel(qs<HTMLDivElement>(backdrop, '#sheetPanel'));
  const close = () => {
    panel.destroy();
    backdrop.remove();
    onClose();
  };
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  qs<HTMLButtonElement>(backdrop, '#closeStockCheck').addEventListener('click', close);
  panel.focus();
}

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'Network error.';
  if (err instanceof ApiError) return `${err.message}${err.code ? ` (${err.code})` : ''}`;
  return String(err);
}
